
import { marcaGuardada as lerMarca, trocarMarcal } from '../../lib/semMarcal';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
// App de marca: texto que vai junto do arquivo/link não leva o nome da Marçal.
function _semMarcalNoTexto(o) {
  const m = lerMarca();
  if (!m) return o;
  return { ...o, titulo: trocarMarcal(o.titulo, m), texto: trocarMarcal(o.texto, m) };
}

/**
 * compartilharBlob — helper unificado pra "Salvar / Compartilhar" PDFs
 * do Meu Contador Marçal.
 *
 * Estratégia em camadas:
 *  1. Capacitor nativo (app instalado): usa @capacitor/share quando
 *     o plugin estiver disponível (Capacitor.Plugins.Share). Permite
 *     compartilhar pra WhatsApp, Mail, AirDrop, etc. com dialog nativo.
 *  2. WebShare API (PWA / browser moderno): navigator.share() com
 *     files[]. iOS Safari 15+ e Chrome Android suportam.
 *  3. Fallback: download via blob link (igual comportamento antigo).
 *
 * Sem plugin Capacitor instalado nesta fase — funciona em modo PWA,
 * e quando o build nativo incluir @capacitor/share o caminho 1 ativa.
 *
 * Uso:
 *   import { compartilharOuBaixar } from '../../components/cliente/compartilharBlob';
 *   await compartilharOuBaixar({
 *     blob,            // Blob | ArrayBuffer
 *     nome: 'DAS_2026-06.pdf',
 *     mime: 'application/pdf',
 *     titulo: 'DAS Junho/2026',
 *     texto: 'Sua guia de DAS — Meu Contador Marçal',
 *   });
 */

/**
 * [Bruno 2026-08-26] AQUI ESTAVA O ANDROID TRAVADO.
 *
 * Olhávamos `window.Capacitor.Plugins.Share` — que só existe depois que ALGUÉM
 * importa o pacote do plugin: é o import que roda registerPlugin() e cria o
 * proxy em JS. Este arquivo nunca importava nada, então a propriedade era
 * sempre undefined e o relatório do aparelho dizia "plugin: nao" mesmo com o
 * plugin instalado no app. O caminho nativo estava morto por um import faltando.
 *
 * No iOS isso passava despercebido porque o WKWebView tem navigator.share com
 * arquivos (caminho 1) e nunca precisou do plugin. O WebView do Android NÃO tem
 * Web Share API — ele depende exatamente deste caminho, e por isso "o iOS faz e
 * o Android trava".
 *
 * Agora o import é estático (no topo) e a pergunta certa é isPluginAvailable,
 * que responde pela implementação NATIVA presente no binário.
 */
/**
 * O Capacitor do app: em runtime nativo, `window.Capacitor` e o objeto importado
 * são o mesmo. Preferimos o global quando existe porque é ele que os testes
 * conseguem montar — e porque, se um dia o bundle for servido sem o pacote, o
 * global ainda responde.
 */
function _cap() {
  const g = (typeof window !== 'undefined' && window.Capacitor) || null;
  return g || Capacitor || null;
}

/**
 * O plugin está no binário? Duas formas de perguntar a mesma coisa:
 * isPluginAvailable é a oficial; Plugins[nome] é a antiga, mantida porque
 * ainda é assim que parte dos testes monta o ambiente.
 */
function _pluginDisponivel(nome) {
  const c = _cap();
  if (!c) return false;
  try {
    if (!c.isNativePlatform?.()) return false;
    if (typeof c.isPluginAvailable === 'function') return !!c.isPluginAvailable(nome);
    return !!(c.Plugins && c.Plugins[nome]);
  } catch { return false; }
}

function _temCapacitorShare() { return _pluginDisponivel('Share'); }
function _temCapacitorFilesystem() { return _pluginDisponivel('Filesystem'); }

/**
 * [Bruno 2026-08-31] O plugin resolvido NA HORA DA CHAMADA, não no import.
 *
 * O `registerPlugin` do Capacitor decide se fala com o NATIVO ou com a versão
 * de navegador **no instante em que o pacote é importado**, olhando a lista que
 * a ponte injeta na página. Se o nosso bundle rodar antes de a ponte terminar de
 * se instalar (o app carrega a tela do nosso servidor, não de dentro do APK),
 * aquele `Share` importado fica preso na versão de navegador PARA SEMPRE — e a
 * versão de navegador do Android não compartilha arquivo nenhum.
 *
 * `window.Capacitor.Plugins.X` não tem esse problema: resolve toda vez que é
 * lido. Preferimos ele e caímos no import quando não existe (navegador comum e
 * os testes).
 */
function _pluginRuntime(nome, importado) {
  try {
    const p = (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.Plugins)
      ? window.Capacitor.Plugins[nome]
      : null;
    if (p && (typeof p === 'object' || typeof p === 'function')) return p;
  } catch (_) { /* segue pro import */ }
  return importado;
}

/**
 * [Bruno 2026-08-31] A CAUSA RAIZ, medida no aparelho do Bruno.
 *
 * O relatório do app veio assim: `Filesystem` e `Share` na lista, e a chamada
 * respondendo **"Filesystem plugin is not implemented on android"**. As duas
 * coisas juntas parecem contradição e não são: a lista que a gente via era a
 * dos pacotes que o NOSSO JavaScript importou, não a do que o aplicativo tem
 * instalado.
 *
 * O que o aparelho tem é isto: `window.androidBridge` (o canal que o Android
 * abre pro app conversar com a parte nativa) EXISTE, mas o script que o
 * Capacitor injeta na página, o que traz a LISTA de plugins e a função de
 * chamada, NUNCA foi injetado. O motivo é o desenho do app: ele não carrega a
 * tela de dentro do APK, carrega do nosso servidor, e nesse caminho a injeção
 * não acontece. Sem essa lista, o pacote decide, no instante do import, que vai
 * falar com a "versão de navegador" do plugin, e a versão de navegador do
 * Filesystem simplesmente não existe no Android. Daí a recusa.
 *
 * Efeito: o app tem o canal aberto e ninguém falava por ele.
 *
 * Então falamos. O protocolo abaixo é o mesmo do arquivo oficial do Capacitor
 * (`native-bridge.js`): manda `{callbackId, pluginId, methodName, options}` pelo
 * `androidBridge.postMessage` e recebe a resposta em `androidBridge.onmessage`
 * (aparelho novo) ou em `Capacitor.fromNative` (aparelho antigo). Os dois estão
 * ligados aqui porque qual deles responde depende da versão do WebView.
 *
 * Se o plugin não estiver dentro do APK, quem recusa passa a ser o ANDROID, com
 * a frase dele ("Plugin not implemented"), e aí sim a conclusão é "precisa de
 * versão nova na loja". Antes dessa ponte não dava pra distinguir uma coisa da
 * outra.
 */
const _chamadasNativas = new Map();
let _seqChamada = Math.floor(Math.random() * 134217728);
// A marca de "já instalei" mora NO objeto da ponte, não numa variável nossa: o
// Android pode trocar o objeto (recarregar a tela troca), e uma variável diria
// que está instalado numa ponte que não existe mais, deixando toda chamada sem
// resposta. Foi exatamente esse engano que o teste pegou.
const _MARCA_PONTE = '__marcalPonteInstalada';

function _ponteCrua() {
  try {
    const ab = typeof window !== 'undefined' ? window.androidBridge : null;
    return ab && typeof ab.postMessage === 'function' ? ab : null;
  } catch (_) { return null; }
}

/** A ponte OFICIAL existe? É ela que declara os plugins do binário. */
function _temHeaderNativo(nome) {
  try {
    const h = (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.PluginHeaders) || null;
    return Array.isArray(h) && h.some((x) => x && x.name === nome);
  } catch (_) { return false; }
}

function _instalarPonte() {
  const ab = _ponteCrua();
  if (!ab) return false;
  if (ab[_MARCA_PONTE]) return true;
  const receber = (r) => {
    if (!r || r.callbackId === undefined) return;
    const guardada = _chamadasNativas.get(String(r.callbackId));
    if (!guardada) return;
    _chamadasNativas.delete(String(r.callbackId));
    if (r.success) guardada.resolve(r.data);
    else guardada.reject(new Error((r.error && (r.error.message || r.error)) || 'o aparelho recusou sem dizer o motivo'));
  };
  // Aparelho novo responde por aqui...
  try {
    const antes = ab.onmessage;
    ab.onmessage = function (ev) {
      try { receber(JSON.parse(ev && ev.data)); } catch (_) { /* resposta de outro dono */ }
      if (typeof antes === 'function') { try { antes.call(this, ev); } catch (_) { /* segue */ } }
    };
  } catch (_) { /* segue: ainda resta o caminho antigo */ }
  // ...e o antigo, por aqui. Não dá pra escolher: depende da versão do WebView.
  try {
    const cap = (typeof window !== 'undefined' && window.Capacitor) || null;
    if (cap) {
      const antes = typeof cap.fromNative === 'function' ? cap.fromNative : null;
      cap.fromNative = (r) => {
        receber(r);
        if (antes) { try { antes(r); } catch (_) { /* segue */ } }
      };
    }
  } catch (_) { /* segue */ }
  try { ab[_MARCA_PONTE] = true; } catch (_) { /* ponte congelada: reinstala toda vez, sem prejuízo */ }
  return true;
}

// O Android responde recusa na hora ("unable to find plugin"), então esperar
// muito só serviria pra deixar o botão girando quando algo trava de vez.
const _ESPERA_NATIVA_MS = 8000;

function _chamarNativo(plugin, metodo, opcoes) {
  const cap = (typeof window !== 'undefined' && window.Capacitor) || null;
  // Ponte oficial montada (app que carrega a tela de dentro do APK): usa ela.
  if (cap && typeof cap.nativePromise === 'function') {
    return cap.nativePromise(plugin, metodo, opcoes || {});
  }
  if (!_instalarPonte()) return Promise.reject(new Error('sem ponte nativa neste aparelho'));
  return new Promise((resolve, reject) => {
    const callbackId = String(++_seqChamada);
    // Sem prazo, um plugin que não responde deixaria o botão girando pra sempre.
    const relogio = setTimeout(() => {
      if (_chamadasNativas.delete(callbackId)) {
        reject(new Error(`${plugin}.${metodo}: o aparelho não respondeu`));
      }
    }, _ESPERA_NATIVA_MS);
    const fim = (fn) => (v) => { clearTimeout(relogio); fn(v); };
    _chamadasNativas.set(callbackId, { resolve: fim(resolve), reject: fim(reject) });
    try {
      _ponteCrua().postMessage(JSON.stringify({
        callbackId, pluginId: plugin, methodName: metodo, options: opcoes || {},
      }));
    } catch (err) {
      _chamadasNativas.delete(callbackId);
      clearTimeout(relogio);
      reject(err);
    }
  });
}

/**
 * O plugin de verdade: pacote importado quando a ponte oficial existe, conversa
 * direta com o Android quando não existe.
 */
function _plugin(nome, importado) {
  if (_temHeaderNativo(nome)) return _pluginRuntime(nome, importado);
  if (_ponteCrua()) {
    return new Proxy({}, {
      get: (_alvo, metodo) => (opcoes) => _chamarNativo(nome, String(metodo), opcoes),
    });
  }
  return _pluginRuntime(nome, importado);
}

function _share() { return _plugin('Share', Share); }
function _fs() { return _plugin('Filesystem', Filesystem); }

function _ehAppNativo() {
  const c = _cap();
  try { return !!c?.isNativePlatform?.(); } catch { return false; }
}

/**
 * [Bruno 2026-08-31] PERGUNTAR NÃO BASTA, TEM QUE TENTAR.
 *
 * O aparelho respondia `plugin: nao` de novo, e em 26/08 essa mesma resposta
 * estava ERRADA (faltava o import, o plugin existia). Uma pergunta que já mentiu
 * uma vez não pode ser o que impede o app de tentar: agora o caminho nativo é
 * TENTADO em todo app instalado, e só o erro de verdade (o que a ponte nativa
 * devolve) conta como ausência. O que a sondagem responde vira só diagnóstico.
 */
function _erro(e) {
  const m = String(e?.message || e?.errorMessage || e || 'sem mensagem');
  return m.length > 90 ? m.slice(0, 90) : m;
}

function _ehCancelamento(e) {
  return /cancel|abort|dismiss/i.test(String(e?.message || e?.name || e || ''));
}

/**
 * Os plugins que a ponte nativa declara ter no binário instalado.
 *
 * É isto que diz, num print, se a versão que o cliente tem na mão foi compilada
 * com o compartilhamento. `PluginHeaders` é a lista que o próprio Android injeta
 * na página; se ela vem vazia, o problema é a ponte, não o plugin.
 */
/**
 * ⚠️ [Bruno 2026-08-31] SÃO DUAS LISTAS DIFERENTES, e juntar as duas me fez
 * errar o diagnóstico uma vez.
 *
 * `PluginHeaders` é o que o APLICATIVO INSTALADO declara ter dentro dele. É a
 * única que responde "a versão do Thiago trouxe o compartilhamento?".
 *
 * `Plugins` é o que o NOSSO JavaScript importou. Ali aparecem Filesystem e Share
 * só porque este arquivo os importa, e ainda aparecem CapacitorCookies,
 * CapacitorHttp e WebView, que o Capacitor cria sozinho. Somando as duas, uma
 * lista cheia parecia prova de que o app tinha os plugins, e não era.
 *
 * Agora saem separadas, e o relatório diz de qual delas cada nome veio.
 */
export function pluginsDeclarados() {
  const nomes = new Set();
  const alvos = [typeof window !== 'undefined' ? window.Capacitor : null, Capacitor];
  for (const c of alvos) {
    if (!c) continue;
    try {
      if (Array.isArray(c.PluginHeaders)) c.PluginHeaders.forEach((h) => h?.name && nomes.add(h.name));
    } catch (_) { /* segue */ }
  }
  return [...nomes].sort();
}

/** O que o nosso bundle importou. Serve pra contrastar, não pra concluir. */
export function pluginsDoJs() {
  const nomes = new Set();
  const alvos = [typeof window !== 'undefined' ? window.Capacitor : null, Capacitor];
  for (const c of alvos) {
    if (!c) continue;
    try {
      if (c.Plugins) Object.keys(c.Plugins).forEach((n) => nomes.add(n));
    } catch (_) { /* segue */ }
  }
  return [...nomes].sort();
}

/**
 * Versão do app instalada NESTE aparelho (não a do servidor).
 *
 * Sem isto não dá pra saber se a atualização que o Thiago publicou chegou no
 * celular: a tela vem do nosso deploy e parece nova mesmo em binário velho.
 */
export async function versaoDoApp() {
  if (!_ehAppNativo()) return null;
  try {
    const info = await App.getInfo();
    return `${info?.version || '?'}(${info?.build || '?'})`;
  } catch (err) {
    return 'erro:' + _erro(err);
  }
}

/**
 * A folha de compartilhamento do sistema existe, nem que seja só pra TEXTO?
 *
 * [Bruno 2026-08-24] Isto é diferente de compartilhar ARQUIVO, e a diferença é o
 * que destrava o Android que não tem plugin nenhum: um aparelho pode recusar
 * `files` e ainda assim abrir a folha nativa com um texto. Se abre, dá pra
 * compartilhar o LINK do documento — que é compartilhar de verdade, pela folha
 * do sistema, sem plugin.
 *
 * O relatório antigo colapsava as duas perguntas num "webshare: nao" só, e foi
 * por isso que eu conclui cedo demais que não havia saída nenhuma.
 */
function _temShareDeTexto() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

function _temWebShareComArquivos() {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;
  try {
    // canShare exige um objeto válido
    return navigator.canShare({ files: [new File([new Blob(['x'])], 't.txt', { type: 'text/plain' })] });
  } catch { return false; }
}

/**
 * O link de download funciona neste aparelho?
 *
 * [Bruno 2026-08-24] NÃO no WebView do Android. Um `<a download>` só baixa se o
 * app nativo tiver instalado um "recebedor de downloads" (DownloadListener), e o
 * Capacitor não instala nenhum; endereço `blob:` então não desce de jeito
 * nenhum. O clique acontece, nada baixa, e ninguém fica sabendo.
 *
 * Era esse o "o botão não faz nada": o último caminho da escada é silencioso
 * justamente no aparelho onde os dois primeiros costumam faltar.
 */
function _downloadFunciona() {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  const ehAppNativo = !!cap?.isNativePlatform?.();
  const ehAndroid = cap?.getPlatform
    ? cap.getPlatform() === 'android'
    : /Android/i.test(navigator.userAgent || '');
  return !(ehAppNativo && ehAndroid);
}

/**
 * O que ESTE aparelho oferece. Vai junto na resposta pra quem chama poder dizer
 * ao cliente o que aconteceu — e pra nós sabermos, por um print, qual caminho
 * falta num aparelho que não temos na mão.
 */
export function capacidadesDeEnvio() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  return {
    plataforma: cap?.getPlatform ? cap.getPlatform() : 'web',
    appNativo: !!cap?.isNativePlatform?.(),
    webshare: _temWebShareComArquivos(),   // aceita ARQUIVO
    share: _temShareDeTexto(),             // abre a folha, nem que seja só texto
    copiar: typeof navigator !== 'undefined' && !!(navigator.clipboard && navigator.clipboard.writeText),
    pluginShare: _temCapacitorShare(),
    pluginArquivos: _temCapacitorFilesystem(),
    download: _downloadFunciona(),
    // O que o APLICATIVO declara ter (única lista que responde se a versão nova
    // trouxe o compartilhamento) e, à parte, o que o nosso JS importou.
    plugins: pluginsDeclarados(),
    pluginsJs: pluginsDoJs(),
    // Qual ponte o aparelho tem: 'oficial' (o Capacitor injetou a lista de
    // plugins), 'crua' (só o canal do Android, e nós falamos por ele) ou
    // 'nenhuma'. É a diferença entre "falta versão nova na loja" e "era o app
    // não estar conversando com a parte nativa".
    ponte: (typeof window !== 'undefined' && window.Capacitor && Array.isArray(window.Capacitor.PluginHeaders) && window.Capacitor.PluginHeaders.length)
      ? 'oficial'
      : (_ponteCrua() ? 'crua' : 'nenhuma'),
  };
}

function _baixar(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click();
  a.remove();
  // Revoga depois pra não interromper download em alguns browsers
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Mostra dialog nativo de compartilhamento (WhatsApp, Email, Salvar
 * em Arquivos, AirDrop). Se nenhum método de compartilhamento estiver
 * disponível, faz download direto. Não joga — sempre faz algo.
 *
 * Retorna { metodo: 'capacitor'|'webshare'|'download', ok: bool }.
 */
export async function compartilharOuBaixar({ blob, nome, mime = 'application/pdf', titulo, texto, preferirDownload = false }) {
  if (!blob) throw new Error('blob obrigatório');
  ({ titulo, texto } = _semMarcalNoTexto({ titulo, texto }));
  // Aceita ArrayBuffer ou Uint8Array também
  const b = blob instanceof Blob ? blob : new Blob([blob], { type: mime });
  // O que cada caminho respondeu. Vai na resposta pra virar telemetria: sem isso
  // "não compartilhou" chega aqui sem motivo, e a investigação recomeça do zero.
  const tentativas = [];

  if (preferirDownload && _downloadFunciona()) {
    _baixar(b, nome);
    return { metodo: 'download', ok: true };
  }

  // [2026-07-23, Bruno] BUG "manda letra/código no WhatsApp, não a nota":
  // o app instalado é NATIVO (Capacitor). O caminho antigo mandava o PDF pro
  // @capacitor/share como `files: ['data:...;base64,...']`, mas o plugin v6 NÃO
  // aceita data URL como arquivo → despejava o base64 do PDF como TEXTO. Por
  // isso chegava "código" no lugar da nota.
  //
  // Ordem nova:
  //  1) WebShare API (navigator.share com files) — abre a MESMA folha nativa do
  //     iOS/Android e ANEXA o PDF de verdade; funciona no WKWebView (iOS 15+) e
  //     no WebView Android moderno. É o que conserta a base HOJE, sem rebuild.
  //  2) Capacitor Share — só como rede de segurança, e NUNCA mais com data URL:
  //     se o @capacitor/filesystem estiver no app, grava o PDF e compartilha o
  //     file:// (anexo real); senão compartilha só a legenda (sem despejar código).
  //  3) Download.

  // 1) WebShare API com arquivos (anexa o PDF)
  if (_temWebShareComArquivos()) {
    const file = new File([b], nome, { type: mime });
    // WhatsApp descarta o anexo quando vem `text`/`title` junto → compartilha SÓ
    // o arquivo. A legenda o cliente digita no próprio WhatsApp.
    try {
      await navigator.share({ files: [file] });
      return { metodo: 'webshare', ok: true };
    } catch (err) {
      if (err?.name === 'AbortError') return { metodo: 'webshare', ok: false, cancelado: true };
      // Alguns alvos (Mail) aceitam melhor com metadados — 2ª tentativa.
      try {
        await navigator.share({ title: titulo, text: texto, files: [file] });
        return { metodo: 'webshare', ok: true };
      } catch (err2) {
        if (err2?.name === 'AbortError') return { metodo: 'webshare', ok: false, cancelado: true };
        tentativas.push('webshare: ' + _erro(err2));
        // cai pro Capacitor / download
      }
    }
  }

  // 2) Plugin nativo. TENTADO SEMPRE que o app é o instalado, mesmo com a
  // sondagem dizendo que não tem: em 26/08 ela disse "plugin: nao" com o plugin
  // presente, e foi ela que segurou o Android por dois meses. O que decide agora
  // é a ponte nativa responder, não a lista responder.
  if (_ehAppNativo()) {
    for (const pasta of _PASTAS_COMPARTILHAR) {
      if (!pasta.dir) continue;
      let fileUri = null;
      try {
        fileUri = await _gravarArquivoCapacitor(b, nome, pasta.dir);
      } catch (err) {
        tentativas.push(`arquivos(${pasta.rotulo}): ` + _erro(err));
        // Plugin fora do aplicativo: trocar de pasta não muda nada.
        if (_pluginAusente(err)) break;
        continue;
      }
      if (!fileUri) { tentativas.push(`arquivos(${pasta.rotulo}): sem uri`); continue; }
      try {
        await _share().share({
          title: titulo, text: texto, url: fileUri,
          dialogTitle: titulo || 'Compartilhar',
        });
        return { metodo: 'capacitor-arquivo', ok: true, tentativas };
      } catch (err) {
        if (_ehCancelamento(err)) return { metodo: 'capacitor-arquivo', ok: false, cancelado: true };
        tentativas.push(`share(${pasta.rotulo}): ` + _erro(err));
        // Recusa que não é da pasta (plugin ausente, folha indisponível): parar.
        if (!_ehErroDeProvedor(err)) break;
      }
    }
  }

  // 3) Download direto — mas SÓ onde ele de fato baixa.
  if (_downloadFunciona()) {
    _baixar(b, nome);
    return { metodo: 'download', ok: true };
  }

  // 4) Acabaram os caminhos. Antes isto era `_baixar()` seguido de `ok: true` —
  // clique no vazio anunciado como sucesso. Falhar em silêncio num botão é pior
  // que falhar: o cliente fica achando que o app travou. Agora quem chamou
  // recebe a recusa e as capacidades do aparelho, e mostra isso na tela.
  //
  // `tentativas` viaja junto: é a fala da ponte nativa ("Share does not have an
  // implementation" é ausência no binário; qualquer outra coisa é bug nosso), e
  // sem ela o diagnóstico volta a depender de adivinhação.
  return { metodo: 'nenhum', ok: false, semCaminho: true, tentativas, capacidades: capacidadesDeEnvio() };
}

/**
 * Grava o blob no cache do app via @capacitor/filesystem e devolve o file://
 * URI (pro Share anexar de verdade). Retorna null se o plugin não estiver
 * disponível no binário instalado — aí o chamador cai no fallback de legenda.
 * Fica pronto pra quando o app for reconstruído com @capacitor/filesystem.
 */
async function _paraBase64(b) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(b);
  });
}

function _nomeSeguro(nome) {
  return (nome || 'arquivo.pdf').replace(/[^\w.\-]+/g, '_');
}

async function _gravarArquivoCapacitor(b, nome, pasta) {
  // [Bruno 2026-08-31] A sondagem do plugin de arquivos saiu daqui: ela só
  // informa, não veta. Se o plugin não estiver no binário, quem diz isso é a
  // própria chamada, com mensagem, e a mensagem é o diagnóstico.
  if (!_ehAppNativo()) return null;
  const dir = pasta || Directory.Cache;
  const base64 = await _paraBase64(b);
  const nomeSeguro = _nomeSeguro(nome);
  // Directory.Cache (enum do plugin), não a string 'CACHE': o plugin v6 valida
  // o valor e devolve erro quando recebe texto solto.
  //
  // Estoura pra fora quando falha: o chamador guarda o motivo. Engolir aqui foi
  // o que fez "arquivos: nao" parecer conclusão quando era só silêncio.
  await _fs().writeFile({ path: nomeSeguro, data: base64, directory: dir });
  const { uri } = await _fs().getUri({ path: nomeSeguro, directory: dir });
  return uri || null;
}

/**
 * [Bruno 2026-08-31] POR QUE A ESCADA DE PASTAS TAMBÉM AQUI.
 *
 * Pra outro aplicativo (o WhatsApp) receber o nosso arquivo, o Android exige que
 * a pasta onde ele está esteja declarada numa lista dentro do app (o
 * "FileProvider"). Essa lista é escrita quando o projeto Android é criado, e a
 * do Capacitor cobre o cache e o armazenamento externo, mas isso é escolha do
 * projeto e mora na máquina de quem compila, fora deste repositório.
 *
 * Se a pasta não estiver na lista, o Android recusa com "Failed to find
 * configured root" e o compartilhamento morre DEPOIS do rebuild, por um detalhe
 * que ninguém lembraria de olhar. Então tentamos as pastas em ordem: se a recusa
 * for desse tipo, a próxima pasta resolve sozinha.
 */
const _PASTAS_COMPARTILHAR = [
  { dir: Directory.Cache, rotulo: 'cache' },
  { dir: Directory.External, rotulo: 'externa' },
  { dir: Directory.Data, rotulo: 'do app' },
];

function _ehErroDeProvedor(err) {
  return /configured root|fileprovider|illegalargument/i.test(String(err?.message || err || ''));
}

/**
 * As três formas de o app dizer "esse plugin não está aqui", e cada uma vem de
 * um lugar: a primeira é do Android ("unable to find plugin : Filesystem", que
 * foi o que o aparelho do Bruno respondeu), as outras duas são do próprio
 * Capacitor no JavaScript. Trocar de pasta não muda nenhuma delas.
 */
function _pluginAusente(err) {
  return /unable to find plugin|not implemented|have an implementation|não implement/i
    .test(String(err?.message || err || ''));
}

/**
 * SALVAR no aparelho, de verdade.
 *
 * [Bruno 2026-08-31] No iOS, "Salvar" é a folha do sistema com "Salvar em
 * Arquivos" — e é assim que continua lá. No Android do app não havia caminho: o
 * link de download não baixa dentro do WebView (o Capacitor não instala
 * recebedor de download), então o botão clicava no vazio.
 *
 * Aqui o arquivo é ESCRITO na pasta do aparelho pelo plugin de arquivos, e o
 * nome da pasta volta pra tela dizer onde ficou. Sem isso, "salvou" é uma
 * palavra que o cliente não consegue conferir.
 *
 * Tenta Documentos (visível pelo gerenciador de arquivos) e cai pra pasta do
 * app quando o sistema recusa — o que muda é onde ficou, não se salvou.
 */
const _PASTAS_SALVAR = [
  { dir: Directory.Documents, rotulo: 'Documentos' },
  { dir: Directory.External, rotulo: 'Arquivos do app' },
  { dir: Directory.Data, rotulo: 'Arquivos do app' },
];

export async function salvarNoAparelho({ blob, nome, mime = 'application/pdf' }) {
  const b = blob instanceof Blob ? blob : new Blob([blob], { type: mime });
  if (!_ehAppNativo()) return { ok: false, motivo: 'nao_e_app' };
  const tentativas = [];
  let base64;
  try {
    base64 = await _paraBase64(b);
  } catch (err) {
    return { ok: false, motivo: 'leitura', tentativas: ['ler: ' + _erro(err)] };
  }
  const nomeSeguro = _nomeSeguro(nome);
  for (const pasta of _PASTAS_SALVAR) {
    if (!pasta.dir) continue;              // enum ausente no plugin: pula
    try {
      await _fs().writeFile({ path: nomeSeguro, data: base64, directory: pasta.dir, recursive: true });
      return { ok: true, pasta: pasta.rotulo, nome: nomeSeguro, tentativas };
    } catch (err) {
      tentativas.push(`${pasta.rotulo}: ${_erro(err)}`);
    }
  }
  return { ok: false, motivo: 'sem_arquivos', tentativas };
}

/**
 * [Bruno 2026-10-09] "NO MODO WEB NÃO ESTÁ CONSEGUINDO SALVAR NOS ARQUIVOS."
 *
 * O botão Salvar fora do app Android chamava a MESMA escada do Compartilhar. No
 * navegador comum que tem a folha de compartilhamento com arquivo (Chrome e Edge
 * no Windows, Safari no Mac e no iPhone), o primeiro degrau é a folha — então
 * "Salvar" abria o painel de compartilhar do sistema, que no Windows não tem
 * "salvar" nenhum (Mail, Phone Link…). O cliente fechava o painel, a função
 * devolvia `cancelado` e a tela ficava calada: nada salvo, nenhum aviso.
 *
 * Fora do app, o navegador TEM o jeito certo de salvar: o download. É o que
 * sempre valeu no PC sem folha, e passa a valer também onde a folha existe.
 *
 * Fica de fora, de propósito, a PWA instalada na tela inicial do iPhone: lá o
 * download não aparece (foi o motivo do Salvar virar folha em 10/07/2026) e a
 * folha é o único caminho ("Salvar em Arquivos"). O app instalado (Capacitor)
 * também segue pelos caminhos nativos.
 */
export function ehNavegadorComum() {
  if (typeof window === 'undefined') return false;
  if (_ehAppNativo()) return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return false;
  } catch (_) { /* sem matchMedia: segue */ }
  try {
    if (typeof navigator !== 'undefined' && navigator.standalone === true) return false;
  } catch (_) { /* segue */ }
  return true;
}

/**
 * SALVAR o arquivo, com a intenção de salvar (não de compartilhar).
 *
 * Navegador comum → download, sem passar pela folha. Qualquer outro lugar
 * (app instalado, PWA na tela inicial) → a escada de sempre, que lá é o que
 * salva ("Salvar em Arquivos" na folha do iOS; Documentos no Android pelo
 * plugin, via `salvarNoAparelho`, que o PreviewPdf chama antes).
 *
 * Mesmo formato de resposta do `compartilharOuBaixar`.
 */
export async function salvarArquivo({ blob, nome, mime = 'application/pdf', titulo, texto }) {
  if (!blob) throw new Error('blob obrigatório');
  if (ehNavegadorComum() && _downloadFunciona()) {
    const b = blob instanceof Blob ? blob : new Blob([blob], { type: mime });
    _baixar(b, nome);
    return { metodo: 'download', ok: true, nome };
  }
  return compartilharOuBaixar({ blob, nome, mime, titulo, texto });
}

/**
 * Variante: força download sem tentar compartilhar. Útil pra botões
 * "Salvar no dispositivo" separados do "Compartilhar".
 */
export function baixarBlob(blob, nome) {
  const b = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/octet-stream' });
  _baixar(b, nome);
}

/**
 * Compartilha um LINK pela folha do sistema — o caminho de quem não consegue
 * entregar arquivo. Cai pra área de transferência quando não há folha.
 *
 * @returns {'folha'|'copiado'|'nenhum'}
 */
export async function compartilharLink({ url, titulo }) {
  ({ titulo } = _semMarcalNoTexto({ titulo }));
  const texto = titulo ? `${titulo}\n${url}` : url;
  if (_temShareDeTexto()) {
    try {
      // `url` separado quando o alvo entende; alguns só olham o `text`, então
      // ele vai junto e completo.
      await navigator.share({ title: titulo || undefined, text: texto, url });
      return 'folha';
    } catch (err) {
      if (err?.name === 'AbortError') return 'folha';   // desistiu, não é falha
      // segue pra área de transferência
    }
  }

  // [Bruno 2026-08-31] A folha do sistema pelo PLUGIN, tentada de verdade.
  //
  // O WebView do Android não tem navigator.share, então o degrau de cima nunca
  // vale lá. Se o app instalado tiver o plugin de compartilhamento (e ele não
  // precisa do de arquivos pra mandar um link), isto abre a folha nativa e o
  // cliente escolhe o WhatsApp, que é o que ele queria fazer. Sem perguntar
  // antes: quem responde é a chamada.
  if (_ehAppNativo()) {
    try {
      await _share().share({ title: titulo || undefined, text: texto, url, dialogTitle: titulo || 'Compartilhar' });
      return 'folha';
    } catch (err) {
      if (_ehCancelamento(err)) return 'folha';
      // sem plugin no binário: segue pra área de transferência
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(texto); return 'copiado'; } catch (_) { /* segue */ }
  }
  // Último recurso sem API nenhuma: campo escondido + execCommand, que é o que
  // WebView antigo ainda entende.
  try {
    const a = document.createElement('textarea');
    a.value = texto; a.style.position = 'fixed'; a.style.opacity = '0';
    document.body.appendChild(a); a.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(a);
    if (ok) return 'copiado';
  } catch (_) { /* nada a fazer */ }
  return 'nenhum';
}

export function compartilhamentoDisponivel() {
  return _temCapacitorShare() || _temWebShareComArquivos();
}

/**
 * [Bruno 2026-09-10] A PERGUNTA FEITA ANTES DO CLIQUE, NÃO DEPOIS DELE.
 *
 * O print que chegou (W2 Educacional, DARF de agosto) é o de um cliente que
 * tocou em "Compartilhar", esperou, e recebeu uma tarja VERMELHA de erro. A
 * telemetria (`/api/debug/cliente-ui-log`) mostra o mesmo em Luis Wink e na LEW,
 * sempre com a mesma fala do Android: `unable to find plugin : Filesystem`, com
 * a ponte oficial de pé e o aplicativo declarando só
 * `App, CapacitorCookies, CapacitorHttp, Preferences, SplashScreen, StatusBar,
 * WebView`. São 15 aparelhos parados na casca 1.0(2), compilada antes de os
 * plugins saírem de `devDependencies` (31/08). Nenhum JavaScript põe capacidade
 * nativa dentro de um app já instalado.
 *
 * O que DÁ pra consertar é a tela oferecer só o que funciona. Esta função
 * responde, ANTES de o cliente tocar em nada, se esta casca tem como entregar um
 * arquivo — e a resposta vem da lista que o PRÓPRIO APLICATIVO declara
 * (`PluginHeaders`), nunca da lista do nosso JavaScript, que sempre tem os dois
 * e por isso mente.
 *
 * Conservadora de propósito: só responde `true` quando as TRÊS portas estão
 * fechadas (folha do navegador, arquivos e compartilhamento nativos) e a lista
 * do aplicativo veio preenchida. Lista vazia é ponte que ainda não terminou de
 * se instalar — silêncio, não ausência —, e nesse caso a tela segue tentando
 * como sempre tentou.
 */
export function cascaNaoEntregaArquivo() {
  // A folha do navegador anexa arquivo (é o caminho do iOS): entrega.
  if (_temWebShareComArquivos()) return false;
  // Navegador comum baixa pelo link normal.
  if (!_ehAppNativo()) return false;
  const declarados = pluginsDeclarados();
  if (!declarados.length) return false;   // não sabido ≠ não tem
  return !declarados.includes('Filesystem') && !declarados.includes('Share');
}

/**
 * O endereço da loja desta plataforma — o conserto de verdade da casca sem
 * compartilhamento é a versão nova, e ela JÁ ESTÁ PUBLICADA nas duas lojas.
 *
 * Vai como link de verdade (`<a href>`) na tela, e não por `window.open`: o
 * Android do app manda pro navegador/loja qualquer endereço que não seja o do
 * nosso servidor, e é assim que ele abre sem depender de plugin nenhum.
 */
export function linkDaLoja() {
  if (lerMarca()) return null; // app de marca é PWA: não tem loja (nem a do Meu Contador)
  const c = _cap();
  let p = 'web';
  try { p = c?.getPlatform ? c.getPlatform() : 'web'; } catch (_) { /* fica web */ }
  if (p === 'android') return 'https://play.google.com/store/apps/details?id=br.com.marcal.app';
  if (p === 'ios') return 'https://apps.apple.com/app/id6777119365';
  return null;
}
